import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Checkbox,
} from '@chakra-ui/react'
import { Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { loginSchema, type LoginFormData } from '@/lib/validations/auth'
import { useAuthStore } from '@/store/auth'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'

export function LoginForm() {
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const { settings: siteSettings } = useSiteSettings()

  // Theme-aware colors
  const colors = useThemeColors()

  // Get site name and description from .env variables
  const siteName = import.meta.env.VITE_APP_NAME || 'Sign In'
  const siteDescription = import.meta.env.VITE_APP_DESCRIPTION || 'Enter your credentials to access your account'

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError('')
      setIsSubmitting(true)
      console.log('[LoginForm] Starting login for:', data.email)
      // Include remember_me in login credentials
      const loginData = {
        ...data,
        remember_me: rememberMe,
      }
      const result = await login(loginData)

      console.log('[LoginForm] Login result received:', result)
      console.log('[LoginForm] Result properties:', {
        success: result?.success,
        twoFactorSetupRequired: result?.twoFactorSetupRequired,
        twoFactorRequired: result?.twoFactorRequired,
        message: result?.message,
        fullResult: result
      })

      if (result.success) {
        // Normal login success, redirect to dashboard
        console.log('[LoginForm] Login successful, redirecting to dashboard')
        navigate('/dashboard')
      } else if (result.twoFactorSetupRequired) {
        // 2FA setup required (first-time user), redirect to 2FA setup page
        console.log('[LoginForm] 2FA setup required, redirecting to setup page')
        const redirectUrl = `/two-factor/setup?email=${encodeURIComponent(data.email)}&remember_me=${rememberMe ? '1' : '0'}`
        console.log('[LoginForm] Redirect URL:', redirectUrl)
        // Use window.location.href directly for more reliable redirect
        window.location.href = redirectUrl
      } else if (result.twoFactorRequired) {
        // 2FA required (user already has 2FA enabled), redirect to 2FA verification page
        console.log('[LoginForm] 2FA required, redirecting to verification page')
        const redirectUrl = `/two-factor?email=${encodeURIComponent(data.email)}&remember_me=${rememberMe ? '1' : '0'}`
        console.log('[LoginForm] Redirect URL:', redirectUrl)
        // Use window.location.href directly for more reliable redirect
        window.location.href = redirectUrl
      } else {
        // Show error message if login failed for other reasons
        console.log('[LoginForm] Login failed, showing error:', result.message)
        setError(result.message || 'Login failed')
        setIsSubmitting(false)
      }
    } catch (err: any) {
      console.error('[LoginForm] Login error caught:', err)
      console.error('[LoginForm] Error response:', err.response)
      setIsSubmitting(false)

      // Check if error response contains 2FA setup requirement
      if (err.response?.status === 403 && err.response?.data?.two_factor_setup_required) {
        console.log('[LoginForm] 2FA setup required in error, redirecting')
        const redirectUrl = `/two-factor/setup?email=${encodeURIComponent(data.email)}&remember_me=${rememberMe ? '1' : '0'}`
        // Use window.location.href directly for more reliable redirect
        window.location.href = redirectUrl
        return
      }

      // Handle lockout with remaining time
      if (err.response?.status === 429) {
        const lockoutMinutes = err.response?.data?.lockout_remaining_minutes
        if (lockoutMinutes) {
          setError(`Too many login attempts. Please try again in ${lockoutMinutes} minute(s).`)
        } else {
          setError(err.response?.data?.errors?.[0] || err.response?.data?.message || 'Too many login attempts. Please try again later.')
        }
      } else {
        setError(err.response?.data?.message || err.response?.data?.errors?.[0] || 'Login failed')
      }
    }
  }

  return (
    <Box minH="100vh" bg={colors.bg} display="flex" alignItems="center" justifyContent="center">
      <Card maxW="md" w="full" bg={colors.cardBg}>
        <CardBody p={6}>
          <VStack spacing={4}>
            <Box textAlign="center">
              {/* Logo - Database driven */}
              <Box mb={2} display="flex" justifyContent="center">
                <img
                  src={siteSettings.auth_logo || "/assets/img/als-logo-2.png"}
                  alt={siteName}
                  style={{ maxWidth: '300px', height: 'auto' }}
                />
              </Box>

              {/* Sign In Header */}
              <Heading size="lg" color={colors.heading} mb={1}>
                {siteName}
              </Heading>

              {/* Description */}
              <Text color={colors.text} fontSize="sm">
                {siteDescription}
              </Text>
            </Box>

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <Box as="form" onSubmit={handleSubmit(onSubmit)} w="100%">
              <VStack spacing={3} w="100%">
                <FormControl isInvalid={!!errors.email} isDisabled={isSubmitting}>
                  <FormLabel>Email</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Mail size={20} />
                    </InputLeftElement>
                    <Input
                      {...register('email')}
                      type="email"
                      placeholder="Enter your email"
                      pl="10"
                      isDisabled={isSubmitting}
                    />
                  </InputGroup>
                  <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.password} isDisabled={isSubmitting}>
                  <FormLabel>Password</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Lock size={20} />
                    </InputLeftElement>
                    <Input
                      {...register('password')}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      pl="10"
                      isDisabled={isSubmitting}
                    />
                    <InputRightElement width="3rem">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        p={0}
                        type="button"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        h="1.75rem"
                        minW="1.75rem"
                        isDisabled={isSubmitting}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
                </FormControl>

                <Box
                  mt={1}
                  w="100%"
                  display="grid"
                  gridTemplateColumns="1fr 1fr"
                  gap={3}
                  alignItems="center"
                >
                  <Box textAlign="left">
                    <Checkbox
                      isChecked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      colorScheme="blue"
                      size="sm"
                      isDisabled={isSubmitting}
                    >
                      <Text fontSize="sm" color={colors.link}>
                        Remember me
                      </Text>
                    </Checkbox>
                  </Box>
                  <Box textAlign="right">
                    <Button
                      variant="link"
                      color="blue.500"
                      fontSize="sm"
                      onClick={() => navigate('/forgot-password')}
                      _hover={{ textDecoration: 'underline' }}
                      isDisabled={isSubmitting}
                    >
                      Forgot your password?
                    </Button>
                  </Box>
                </Box>

                <Button
                  type="submit"
                  bg="blue.500"
                  color="white"
                  _hover={{ bg: 'blue.600' }}
                  size="lg"
                  w="full"
                  isLoading={isSubmitting}
                  loadingText="Signing in..."
                  isDisabled={isSubmitting}
                >
                  Sign In
                </Button>
              </VStack>
            </Box>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  )
}

